<template>
<div>
    <h2><i class="fa fa-fw fa-lg fa-user"></i> {{ $label('operator.contact_person') }}</h2>
    <p></p>

    <div class="v-form-partial">
        <div class="v-form">
            <fieldset>
                <div class="row">
                    <!-- BEGIN Last name -->
                    <section class="col col-6">
                        <label class="label">{{ $label('common.last_name') }}</label>

                        <label class="input">
                            <input v-model="contactPerson.last_name"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('last_name') }"
                                :placeholder="$label('common.last_name')"
                                :disabled="updating"
                                type="text"
                            />
                        </label>

                        <p v-if="hasError('last_name')" class="text-danger">{{ getError('last_name') }}</p>
                    </section>
                    <!-- END Last name -->

                    <!-- BEGIN First name -->
                    <section class="col col-6">
                        <label class="label">{{ $label('common.first_name') }}</label>

                        <label class="input">
                            <input v-model="contactPerson.first_name"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('first_name') }"
                                :placeholder="$label('common.first_name')"
                                :disabled="updating"
                                type="text"
                            />
                        </label>

                        <p v-if="hasError('first_name')" class="text-danger">{{ getError('first_name') }}</p>
                    </section>
                    <!-- END First name -->

                </div>

                <div class="row">
                    <!-- BEGIN Email address -->
                    <section class="col col-9">
                        <label class="label">{{ $label('common.email_address_long') }}</label>

                        <label class="input">
                            <i class="icon-append icon-envelope"></i>
                            <input v-model="contactPerson.email"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('email') }"
                                :placeholder="$label('common.email_address_short')"
                                :disabled="updating"
                                type="text"
                            />
                        </label>

                        <p v-if="hasError('email')" class="text-danger">{{ getError('email') }}</p>
                    </section>
                    <!-- END Email address -->

                    <!-- BEGIN Gender -->
                    <section class="col col-3">
                        <div class="btn-group">
                            <label class="label">{{ $label('common.gender') }}</label>
                            <label @click="setGender(2)" class="btn btn-default v-form-button" :class="{ 'active': isFemale, 'is-disabled': updating }">
                                <i class="fa fa-fw fa-lg fa-female"></i>
                            </label>
                            <label @click="setGender(1)" class="btn btn-default v-form-button" :class="{ 'active': isMale, 'is-disabled': updating }">
                                <i class="fa fa-fw fa-lg fa-male"></i>
                            </label>
                        </div>
                    </section>
                    <!-- END Gender -->
                </div>

                <phone-number v-show="contactPerson.id"
                    :component-parent-view="componentParentView"
                    :updating="updating"
                    :resource-id="contactPerson.id"
                    :resource-path="phoneNumberResourcePath">
                </phone-number>

                <!-- </div> -->
            </fieldset>
        </div>
    </div>
</div>
</template>

<script>
import Api from '@/api';
import FormErrorsMixin from '@/mixins/FormErrors.js';
import ModelActionsMixin from '@/mixins/ModelActions.js';

import PhoneNumber from '@/components/Common/Models/PhoneNumber.vue';

export default {
    props: ['parentResourcePath', 'parentResourceId', 'updating', 'componentParentView'],

    mixins: [FormErrorsMixin, ModelActionsMixin],

    components: {
        PhoneNumber,
    },

    data() {
        return {
            validatedInputs: [
                'first_name', 'last_name', 'email', 'gender',
            ],
            errors: [],
            parentView: this.componentParentView,
            modelData: 'contactPerson',
            contactPerson: {
                id: null,
                first_name: null,
                last_name: null,
                email: null,
                gender: null,
            },
        }
    },

    mounted() {
        this.initializeContactPerson();
    },

    methods: {
        initializeContactPerson() {
            if (!this.parentResourceId) {
                return;
            }

            this.resetModel();
            this.getContactPerson();
        },

        getContactPerson(successCallback) {
            let endpoint = this.$partnerEndpoint(this.parentResourcePath + 'contact-person');

            Api().get(endpoint)
                .then((response) => {
                    this.contactPerson = response.data.data;
                    delete this.contactPerson.department;

                    if (typeof successCallback == 'function') {
                        successCallback();
                    } else {
                        this.emitModelLoadedEvent();
                    }
                })
                .catch((error) => {
                    this.resetModel();
                    this.emitModelLoadedEvent(false);
                });
        },

        storeModel() {
            this.resetErrors();
            let endpoint = this.$partnerEndpoint(this.parentResourcePath + 'contact-person');

            if (!this.contactPerson.gender) {
                delete this.contactPerson.gender;
            }

            Api().post(endpoint, this.contactPerson)
                .then((response) => {
                    // Get the contact person id because is required for the phone number, after that emit the updated event
                    this.getContactPerson(() => {
                        this.emitModelUpdatedEvent();
                    });
                })
                .catch((error) => {
                    this.storeErrors(error.response.data.errors);
                    this.emitModelUpdatedEvent(false);
                });
        },

        updateModel() {
            this.resetErrors();
            let endpoint = this.$partnerEndpoint('contact-persons/' + this.contactPerson.id);

            if (!this.contactPerson.gender) {
                delete this.contactPerson.gender;
            }

            Api().put(endpoint, this.contactPerson)
                .then((response) => {
                    this.emitModelUpdatedEvent();
                })
                .catch((error) => {
                    this.storeErrors(error.response.data.errors);
                    this.emitModelUpdatedEvent(false);
                });
        },

        deleteModel() {
            this.resetErrors();
            let endpoint = this.$partnerEndpoint('contact-persons/' + this.contactPerson.id);

            Api().delete(endpoint)
                .then((response) => {
                    this.contactPerson.id = null;
                    this.emitModelUpdatedEvent();
                })
                .catch((error) => {
                    this.emitModelUpdatedEvent(false);
                });
        },

        /**
         * https://en.wikipedia.org/wiki/ISO/IEC_5218
         */
        setGender(gender) {
            this.contactPerson.gender = gender;
        },
    },

    computed: {
        isFemale() {
            return this.contactPerson.gender == 2;
        },

        isMale() {
            return this.contactPerson.gender == 1;
        },

        phoneNumberResourcePath() {
            return 'contact-persons/' + this.contactPerson.id + '/';
        },
    },

    watch: {
        parentResourcePath(parsentResourcePath) {
            this.initializeContactPerson();
        },
    },
}
</script>
